/**
 * The transcript grammar every copy made on this site is written in.
 *
 * It is the golden-corpus grammar of the SDR CW Decoder project (CORPUS.md
 * section 3), because that is where accepted copies end up:
 *
 *   A-Z 0-9 . , ? /    plain characters, one scored token each
 *   =                  BT, the break, one scored token
 *   <AR> <SK> ...      a prosign: its letters run together in angle brackets,
 *                      one scored token
 *   [K]                a best guess at ONE uncertain character. Scored as a
 *                      free position: neither a match nor an error
 *   #                  a stretch that could not be copied, any length. The
 *                      scorer excludes whatever it aligns with
 *
 * Spaces separate words and are not scored. A line break is the same as a
 * space, so a copier may start every over on a new line.
 *
 * The tokenizer mirrors tokenize_ref() in the decoder repo's harness/score.py,
 * so text that passes analyzeTranscript() here scores exactly as it was
 * typed. The checks are deliberately STRICTER than score.py, which warns and
 * then scores malformed input as literal characters: a form that takes copies
 * from other operators should refuse anything it would have to guess about.
 */
/** The characters score.py scores (its CHAR_RE). */
const SCORED_CHAR = /^[A-Z0-9.,?/=]$/;
/** Prosigns an operator will actually meet. Anything else draws a warning. */
export const KNOWN_PROSIGNS = [
    'AR', 'SK', 'KN', 'BK', 'AS', 'HH', 'KA', 'CT', 'SN', 'VE', 'CL', 'SOS',
];
/** The prosign buttons on the copy form, most common first. */
export const PROSIGN_BUTTONS = ['AR', 'SK', 'KN', 'BK', 'AS', 'HH'];
export const MAX_TRANSCRIPT_CHARS = 20_000;
/**
 * Canonical form of a copy: uppercase, `<BT>` written as `=` (CORPUS.md says
 * BT is written `=`), runs of spaces collapsed, lines trimmed, at most one
 * blank line in a row, and `##` folded to `#`. It never changes a character
 * the scorer would read differently.
 */
export function normalizeTranscript(raw) {
    return raw
        .replace(/\r\n?/g, '\n')
        // ASCII only. String.prototype.toUpperCase turns a stray 'ß' into 'SS',
        // which is two valid CW letters nobody sent; left alone, it is flagged.
        .replace(/[a-z]/g, (c) => c.toUpperCase())
        .replace(/<\s*BT\s*>/g, '=')
        .replace(/[^\S\n]+/g, ' ')
        .split('\n')
        .map((line) => line.trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/#+/g, '#')
        .trim();
}
export function analyzeTranscript(raw) {
    const normalized = normalizeTranscript(raw);
    const tokens = [];
    const issues = [];
    const s = normalized;
    const issue = (severity, start, end, message) => {
        issues.push({ severity, message, snippet: s.slice(start, end), start, end });
    };
    if (raw.length > MAX_TRANSCRIPT_CHARS) {
        issue('error', 0, 0, `A copy can be at most ${MAX_TRANSCRIPT_CHARS} characters long.`);
    }
    let i = 0;
    while (i < s.length) {
        const ch = s[i];
        if (ch === ' ' || ch === '\n') {
            i += 1;
            continue;
        }
        if (ch === '<') {
            const close = s.indexOf('>', i + 1);
            const nextOpen = s.indexOf('<', i + 1);
            if (close < 0 || (nextOpen >= 0 && nextOpen < close)) {
                issue('error', i, i + 1, 'An angle bracket was opened and never closed. Prosigns look like <AR>.');
                i += 1;
                continue;
            }
            const inner = s.slice(i + 1, close);
            if (!/^[A-Z]{2,3}$/.test(inner)) {
                issue('error', i, close + 1, `${s.slice(i, close + 1)} is not a prosign. Write a prosign as its two or three letters in angle brackets, like <AR> or <SK>.`);
            }
            else {
                if (!KNOWN_PROSIGNS.includes(inner)) {
                    issue('warning', i, close + 1, `<${inner}> is an unusual prosign. Check it is what you heard.`);
                }
                tokens.push({ kind: 'char', text: `<${inner}>`, start: i, end: close + 1 });
            }
            i = close + 1;
            continue;
        }
        if (ch === '[') {
            const guess = s[i + 1];
            if (guess !== undefined && s[i + 2] === ']' && SCORED_CHAR.test(guess)) {
                tokens.push({ kind: 'free', text: `[${guess}]`, start: i, end: i + 3 });
                i += 3;
                continue;
            }
            const close = s.indexOf(']', i + 1);
            const end = close >= 0 && close - i <= 12 ? close + 1 : i + 1;
            issue('error', i, end, 'Square brackets hold exactly one character, your best guess at it: W7[K]Z. For a stretch you could not copy, use #.');
            i = end;
            continue;
        }
        if (ch === ']' || ch === '>') {
            issue('error', i, i + 1, `A stray ${ch} with nothing opening it.`);
            i += 1;
            continue;
        }
        if (ch === '#') {
            tokens.push({ kind: 'wild', text: '#', start: i, end: i + 1 });
            i += 1;
            continue;
        }
        if (SCORED_CHAR.test(ch)) {
            tokens.push({ kind: 'char', text: ch, start: i, end: i + 1 });
            i += 1;
            continue;
        }
        issue('error', i, i + 1, `${JSON.stringify(ch)} is not a CW character. Use A-Z, 0-9, . , ? / and = for BT.`);
        i += 1;
    }
    if (tokens.length === 0 && s.length === 0) {
        issue('error', 0, 0, 'Nothing copied yet.');
    }
    const stats = {
        scored: tokens.filter((t) => t.kind === 'char').length,
        uncertain: tokens.filter((t) => t.kind === 'free').length,
        gaps: tokens.filter((t) => t.kind === 'wild').length,
        words: s.split(/\s+/).filter((w) => w.length > 0).length,
    };
    return {
        normalized,
        tokens,
        issues,
        stats,
        ok: tokens.length > 0 && !issues.some((x) => x.severity === 'error'),
    };
}
//# sourceMappingURL=grammar.js.map